import { useState } from 'react';
import {
  Alert,
  Dialog,
  RadioGroupControl,
  TextV2,
} from '@insights-ltd/design-library/components';
import { Box, styled } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import {
  permissionContentTitleKeys,
  userPermissionContent,
  AllowedPermissionIcon,
  RestrictedPermissionIcon,
} from 'pages/AddUser/AddUserForm/helper';
import {
  PERMISSION_ROLE_MAP,
  PermissionRole,
} from 'pages/AddUser/AddUserForm/types';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useGetSupportedRoles } from 'api/organisations/organisationHooks';
import { getRolePermissionV2Options } from './helper';

type RolePermissionV2Props = {
  isSuperAdmin: boolean;
  roles?: PermissionRole[];
  roleChangeIsPermitted: boolean | undefined;
  permissionRole: PermissionRole;
  changeRoleType: (value: PermissionRole) => void;
  organisationId?: string;
  disableGMRoleChangeDialog?: boolean;
};

export type PermissionRoleTextType = {
  [key in PermissionRole]: {
    key: string;
    isRestricted?: boolean;
  }[];
};

const StyledList = styled('ul')(({ theme }) => ({
  lineHeight: '20px',
  color: theme.palette && theme.palette.text && theme.palette.text.primary,
  paddingLeft: '0',
}));

const GMRoleChangeWarningDialog = ({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      title={t(
        'ui.event-management.users.invite.permission-roles.group-manager.modal.title',
      )}
      content={
        <Box sx={{ width: '98%' }}>
          <TextV2 color="textSecondary">
            <Trans
              i18nKey={
                'ui.event-management.users.invite.permission-roles.group-manager.modal.text'
              }
              components={{
                bold: <TextV2 variant="bodyBold" component="span" />,
              }}
            />
          </TextV2>
          <Alert severity="warning" sx={{ marginTop: spacingSizeMap.S }}>
            {t(
              'ui.event-management.users.invite.permission-roles.group-manager.modal.warning',
            )}
          </Alert>
        </Box>
      }
      onClose={onClose}
      primaryButtonText={t(
        'ui.event-management.users.invite.permission-roles.group-manager.modal.confirm',
      )}
      secondaryButtonText={t(
        'ui.event-management.users.invite.permission-roles.group-manager.modal.cancel',
      )}
      onSubmit={onConfirm}
    />
  );
};

const RolePermissionV2 = ({
  isSuperAdmin,
  roleChangeIsPermitted,
  permissionRole,
  changeRoleType,
  roles,
  disableGMRoleChangeDialog = false,
  organisationId,
}: RolePermissionV2Props) => {
  const { t } = useTranslation();
  const { data } = useGetSupportedRoles(organisationId, {
    enabled: !!organisationId,
  });
  const apiSupportedRoles = data?.supportedRoles.map(
    (role) => PERMISSION_ROLE_MAP[role],
  );

  const roleOptions = getRolePermissionV2Options(
    t,
    isSuperAdmin,
    apiSupportedRoles || roles,
  );
  const [isGMRoleChangeDialogOpen, setIsGMRoleChangeDialogOpen] =
    useState(false);

  const changeRole = (value: PermissionRole) => {
    if (
      !disableGMRoleChangeDialog &&
      ['STANDARD', 'ADVANCED_USER', 'RESTRICTED_USER'].includes(
        permissionRole,
      ) &&
      value === 'GROUP_MANAGER'
    ) {
      setIsGMRoleChangeDialogOpen(true);
    } else {
      changeRoleType(value);
    }
  };

  return (
    <>
      <Box
        data-testid="role-permission-v2"
        sx={{
          margin: spacingSizeMap.S,
          width: '100%',
        }}
      >
        <TextV2
          variant="bodyBold"
          component="h2"
          data-testid="role-permission-v2-title"
        >
          {t('ui.event-management.users.invite.permission-role.label')}
        </TextV2>
        <RadioGroupControl<PermissionRole>
          data-testid="role-permission-v2-options"
          options={roleOptions}
          disabled={!roleChangeIsPermitted}
          name="roles"
          title={t('ui.event-management.users.create.permission-roles')}
          value={permissionRole}
          onChange={changeRole}
          variant="boxed"
          sx={{
            marginTop: spacingSizeMap.XS,
            '& > div > div:not(:first-of-type)': {
              paddingLeft: `${spacingSizeMap.XS} !important`,
            },
          }}
        />

        <StyledList data-testid="role-permission-v2-text">
          <Trans
            i18nKey={permissionContentTitleKeys[permissionRole]}
            components={{
              bold: <TextV2 variant="bodyBold" component="span" />,
            }}
          />

          {userPermissionContent[permissionRole]?.map(
            ({ key, isRestricted = false }) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    mr: 1,
                  }}
                >
                  {isRestricted ? (
                    <RestrictedPermissionIcon
                      data-testid={`restricted-permission-ICON-${key.replace(
                        /^.*\./,
                        '',
                      )}`}
                    />
                  ) : (
                    <AllowedPermissionIcon
                      data-testid={`allowed-permission-ICON-${key.replace(
                        /^.*\./,
                        '',
                      )}`}
                    />
                  )}
                </Box>
                <Trans
                  i18nKey={key}
                  components={{
                    bold: (
                      <TextV2
                        variant="bodyBold"
                        component="span"
                        sx={{ marginInline: '0.2rem' }}
                      />
                    ),
                  }}
                />
              </Box>
            ),
          )}
        </StyledList>
      </Box>
      <GMRoleChangeWarningDialog
        open={isGMRoleChangeDialogOpen}
        onClose={() => setIsGMRoleChangeDialogOpen(false)}
        onConfirm={() => {
          changeRoleType('GROUP_MANAGER');
          setIsGMRoleChangeDialogOpen(false);
        }}
      />
    </>
  );
};

export default RolePermissionV2;
